import { createContext, useContext } from 'react';

import type { OrganizationFormValues } from '../model/types';

export type WizardStepKey = keyof OrganizationFormValues | 'summary';

export type WizardStep = {
	key: WizardStepKey;
	title: string;
	action?: string;
	link: string;
};

export type WizardStepState = Record<
	WizardStepKey,
	{
		isValid: boolean;
		isVisited: boolean;
	}
>;

export type WizardStepContextState = {
	stepState: WizardStepState;
	setStepState: (value: React.SetStateAction<WizardStepState>) => void;
	isLocked: boolean;
	setIsLocked: (value: React.SetStateAction<boolean>) => void;
};

export const WizardStepsContext = createContext<WizardStepContextState | null>(null);

export const useWizardSteps = () => {
	const context = useContext(WizardStepsContext);
	if (!context) {
		throw new Error('useWizardSteps must be used within a WizardStepsProvider');
	}
	return context;
};

export const useWizardStep = (
	stepKey: WizardStepKey,
	defaultValue?: { isValid: boolean; isVisited: boolean }
) => {
	const { stepState } = useWizardSteps();
	return stepState[stepKey] || defaultValue || { isValid: false, isVisited: false };
};

export const DefaultWizardStepState: WizardStepState = {
	general: { isValid: true, isVisited: false },
	users: { isValid: false, isVisited: false },
	settings: { isValid: false, isVisited: false },
	summary: { isValid: false, isVisited: false },
};

export const DefaultEditWizardStepState: WizardStepState = {
	general: { isValid: true, isVisited: true },
	users: { isValid: true, isVisited: true },
	settings: { isValid: true, isVisited: true },
	summary: { isValid: true, isVisited: true },
};
